/**
 * Fila de documentos do ditado, por paciente (spec 009, fase 3).
 *
 * Vive em memória de módulo — não em estado de componente — por dois motivos
 * que a spec exige:
 *
 *  1. **Vínculo com quem estava na tela.** Cada card guarda o `patientId` e o
 *     `encounterId` do momento em que a gravação COMEÇOU. Se o médico navegar
 *     pra outro paciente, a fila não vai junto: ela fica onde nasceu e
 *     reaparece quando ele voltar (mesmo comportamento do `extractionManager`).
 *  2. **Trava de duplicata.** Dois ditados seguidos do mesmo pedido não podem
 *     virar dois documentos iguais.
 *
 * Some ao recarregar a aba, de propósito: documento preparado e não impresso
 * não é registro clínico, e ressuscitar um pedido de ontem seria pior que
 * perdê-lo.
 */

import { comIntervalo, draftSignature, isDraftEmpty, type VoiceDraft } from './resolveDrafts';
import type { VoiceEyeRx, VoiceGap } from './normalize';
import { examGaps, lensRxGaps, medicationGaps, mergeLensRx, motivoPeloCard, returnGaps, surgeryGaps } from './normalize';
import { solicitacoesLabel } from './resolveDrafts';
import { mergeNotes, withoutRemovedLines } from '@/utils/notesDefaults';
import { mergeExamFill } from './examFill';
import { emptyTextDocDraft, mergeTextDocDrafts, TEXT_DOC_EMPTY_LABEL } from './textDocs';
import { resolveJev } from './jev/log';
import type { JevKind } from '../../../functions/src/domain/voice/jevQuestions';

type ExamDraft = Extract<VoiceDraft, { kind: 'solicitacaoExames' }>;
type MedDraft = Extract<VoiceDraft, { kind: 'receitaMedicamentos' }>;
type LensDraft = Extract<VoiceDraft, { kind: 'receitaOculos' }>;
type ExamValuesDraft = Extract<VoiceDraft, { kind: 'examesAtendimento' }>;

/**
 * Valores de exame ditados em frases ("pressão 14 e 16" pausa "acuidade
 * 20/20"): tudo num card só; célula repetida fica com a última (corrigir).
 * Pendência de um módulo que o ditado novo trouxe ancorado some.
 */
function mergeExamValuesDrafts(existing: ExamValuesDraft, incoming: ExamValuesDraft): ExamValuesDraft {
  const fill = mergeExamFill(existing.fill, incoming.fill);
  const gaps = [
    ...existing.gaps.filter((g) => !Object.keys(incoming.fill).some((id) => g.field === `examesAtendimento.${id}.ancoragem`)),
    ...incoming.gaps,
  ];
  // Nota de origem ("lido da foto da tonometria"): módulo que chegou de novo
  // perde a nota antiga — o valor agora é o que veio por último.
  const sources = {
    ...Object.fromEntries(Object.entries(existing.sources ?? {}).filter(([id]) => !(id in incoming.fill))),
    ...(incoming.sources ?? {}),
  };
  const { sources: _velhas, ...resto } = existing;
  return { ...resto, fill, gaps, ...(Object.keys(sources).length > 0 ? { sources } : {}) };
}

/** Só as pendências de ancoragem sobrevivem à fusão; as de faixa são recalculadas. */
const ancoragemDe = (...ds: VoiceDraft[]) =>
  ds.flatMap((d) => d.gaps).filter((g) => g.field.endsWith('.ancoragem'));

/**
 * Reindexa as pendências de ancoragem de uma lista ao fundir dois cards.
 *
 * Sem isto, o card herdava a acusação de um item que NEM ENTROU: no campo
 * (20/09/2026) o modelo repetiu "Campimetria visual computadorizada" num
 * segundo turno em que ela não foi falada, a duplicata foi descartada na
 * fusão e a pendência dela ficou no card, dizendo que o exame não tinha
 * sido dito enquanto ele estava lá, correto, vindo do primeiro turno.
 *
 * `mantidos` mapeia índice no card NOVO → índice no card de origem.
 */
function remapAnchors(
  gaps: VoiceGap[],
  prefixo: 'exames' | 'medicamentos' | 'cirurgias',
  mantidos: Array<{ de: number; para: number }>,
): VoiceGap[] {
  const porOrigem = new Map(mantidos.map((m) => [m.de, m.para]));
  return gaps.flatMap((g) => {
    const hit = g.field.match(new RegExp(`^${prefixo}\\[(\\d+)\\]\\.ancoragem$`));
    if (!hit) return [];
    const destino = porOrigem.get(Number(hit[1]));
    return destino === undefined ? [] : [{ ...g, field: `${prefixo}[${destino}].ancoragem` }];
  });
}

/** Itens do card de origem que sobreviveram, na ordem em que ficaram. */
function mantidosDe<T>(origem: T[], sobreviventes: T[], base = 0): Array<{ de: number; para: number }> {
  return sobreviventes.map((item, k) => ({ de: origem.indexOf(item), para: base + k }));
}

/**
 * Receita de óculos ditada em frases ("OD …" pausa "OE …" pausa "adição
 * +2,00"): campo novo entra, campo repetido substitui (ditar de novo é
 * corrigir). Regra em `mergeLensRx`.
 */
function mergeLensDrafts(existing: LensDraft, incoming: LensDraft): LensDraft {
  const lens = mergeLensRx(existing.lens, incoming.lens);
  // Ancoragem da refração só sobrevive se NENHUM dos dois turnos falou de
  // grau: basta um ter falado pra a receita se sustentar.
  const ancoragem = ancoragemDe(existing).length > 0 && ancoragemDe(incoming).length > 0
    ? ancoragemDe(existing)
    : [];
  return { kind: 'receitaOculos', label: 'Receita de óculos', lens, gaps: [...lensRxGaps(lens), ...ancoragem] };
}
const normName = (s: string) => s.trim().toLowerCase();

/**
 * Exames ditados em sequência ("OCT de papila." pausa "Retinografia." pausa
 * "Biometria.") são UM pedido, não três: é assim que o médico dita e é assim
 * que o pedido sai à mão. Então exame novo entra no card de exames que já
 * está na fila (e ainda não foi registrado), em vez de abrir outro.
 *
 * Devolve o card fundido, ou `null` quando não havia nada a acrescentar
 * (todos os exames já estavam lá e não veio retorno novo): o chamador trata
 * como duplicata. Decisão do Leopoldo, 16/09/2026.
 */
/**
 * Mesma regra pra receita: "Volata." pausa "Glaucotrat." pausa "Ocupress."
 * é UMA receita com três itens. Item repetido (mesmo nome resolvido, ou o
 * mesmo ouvido quando não resolveu) não entra duas vezes. Decisão do
 * Leopoldo, 16/09/2026.
 */
function mergeMedDrafts(existing: MedDraft, incoming: MedDraft): MedDraft | null {
  const key = (i: MedDraft['items'][number]) => normName(i.match.value?.nome ?? i.match.heard);
  const have = new Set(existing.items.map(key));
  const novos = incoming.items.filter((i) => key(i) && !have.has(key(i)));
  // Linha que o médico tirou deste card (×) não volta com o ditado seguinte.
  const notes = mergeNotes(existing.notes ?? '', withoutRemovedLines(incoming.notes ?? '', existing.tiradas?.notes));
  if (novos.length === 0 && notes === (existing.notes ?? '')) return null;
  const items = [...existing.items, ...novos];
  const ancoragem = [
    ...remapAnchors(existing.gaps, 'medicamentos', mantidosDe(existing.items, existing.items)),
    ...remapAnchors(incoming.gaps, 'medicamentos', mantidosDe(incoming.items, novos, existing.items.length)),
  ];
  const notasDitas = [...(existing.notasDitas ?? []), ...(incoming.notasDitas ?? [])];
  return {
    kind: 'receitaMedicamentos',
    label: 'Receita de medicamentos',
    items,
    notes,
    ...(notasDitas.length > 0 ? { notasDitas } : {}),
    ...(existing.tiradas ? { tiradas: existing.tiradas } : {}),
    // Receituário escolhido no card fica escolhido com o ditado seguinte.
    ...(existing.controlled !== undefined ? { controlled: existing.controlled } : {}),
    ...(incoming.notaIntervalo ?? existing.notaIntervalo
      ? { notaIntervalo: incoming.notaIntervalo ?? existing.notaIntervalo }
      : {}),
    gaps: [...medicationGaps(items), ...ancoragem],
  };
}

function mergeExamDrafts(existing: ExamDraft, incoming: ExamDraft): ExamDraft | null {
  const have = new Set(existing.exams.map((e) => normName(e.nome)));
  const novos = incoming.exams.filter((e) => e.nome.trim() && !have.has(normName(e.nome)));
  // Retorno novo com mês válido substitui; sem mês válido só entra se não havia nenhum.
  // Motivo padrão decide pelo card fundido: o retorno pode ter vindo numa fala
  // sem exame e os exames em outra (`motivoPeloCard`).
  const temExames = existing.exams.length > 0 || novos.length > 0;
  const retorno = motivoPeloCard(
    incoming.retorno && (incoming.retorno.mes.value || !existing.retorno)
      ? incoming.retorno
      : existing.retorno ?? null,
    temExames,
  );
  // Cirurgia: mesma regra dos exames (por texto), pra sair no mesmo PDF.
  const temCir = new Set(existing.surgeries.map((s) => normName(s.texto)));
  const novasCir = incoming.surgeries.filter((s) => !temCir.has(normName(s.texto)));
  const notes = mergeNotes(existing.notes ?? '', withoutRemovedLines(incoming.notes ?? '', existing.tiradas?.notes));
  const surgeryNotes = mergeNotes(
    existing.surgeryNotes ?? '',
    withoutRemovedLines(incoming.surgeryNotes ?? '', existing.tiradas?.surgeryNotes),
  );
  if (
    novos.length === 0 && novasCir.length === 0 && retorno === (existing.retorno ?? null)
    && notes === (existing.notes ?? '') && surgeryNotes === (existing.surgeryNotes ?? '')
  ) return null;
  const exams = [...existing.exams, ...novos];
  const surgeries = [...existing.surgeries, ...novasCir];
  const card = { exams, retorno, surgeries };
  const pacotes = [...new Set([...(existing.pacotes ?? []), ...(incoming.pacotes ?? [])])];
  // A marca "Jev" das orientações escolhidas vem junto com elas.
  const notasDitas = [...(existing.notasDitas ?? []), ...(incoming.notasDitas ?? [])];
  return {
    kind: 'solicitacaoExames',
    label: solicitacoesLabel(card),
    ...card,
    ...(notes ? { notes } : {}),
    ...(surgeryNotes ? { surgeryNotes } : {}),
    ...(notasDitas.length > 0 ? { notasDitas } : {}),
    ...(existing.tiradas ? { tiradas: existing.tiradas } : {}),
    ...(pacotes.length > 0 ? { pacotes } : {}),
    gaps: [
      ...examGaps(exams),
      ...returnGaps(retorno),
      ...surgeries.flatMap((s, i) => surgeryGaps(s, i)),
      ...remapAnchors(existing.gaps, 'exames', mantidosDe(existing.exams, existing.exams)),
      ...remapAnchors(incoming.gaps, 'exames', mantidosDe(incoming.exams, novos, existing.exams.length)),
      ...remapAnchors(existing.gaps, 'cirurgias', mantidosDe(existing.surgeries, existing.surgeries)),
      ...remapAnchors(incoming.gaps, 'cirurgias', mantidosDe(incoming.surgeries, novasCir, existing.surgeries.length)),
    ],
  };
}

/**
 * Receita que chegou a 2+ colírios ganha o intervalo (`comIntervalo`) — no
 * ditado, na fusão e na edição do card (escolher o remédio empatado também
 * conta). Card já registrado não muda.
 */
function comIntervaloNaFila(items: VoiceQueueItem[]): VoiceQueueItem[] {
  return items.map((i) => {
    if (i.registeredAt) return i;
    const draft = comIntervalo(i.draft);
    return draft === i.draft ? i : { ...i, draft };
  });
}

/**
 * Card esvaziado no × que o ditado preenche de novo: o que o médico tirou
 * dele continua tirado (as notas padrão e os modelos voltam em todo ditado).
 */
function herdaTiradas(vazio: VoiceDraft, draft: VoiceDraft): VoiceDraft {
  if (vazio.kind === 'receitaMedicamentos' && draft.kind === 'receitaMedicamentos' && vazio.tiradas) {
    const notes = withoutRemovedLines(draft.notes ?? '', vazio.tiradas.notes);
    return { ...draft, notes: notes || undefined, tiradas: vazio.tiradas };
  }
  if (vazio.kind === 'solicitacaoExames' && draft.kind === 'solicitacaoExames' && vazio.tiradas) {
    const notes = withoutRemovedLines(draft.notes ?? '', vazio.tiradas.notes);
    const surgeryNotes = withoutRemovedLines(draft.surgeryNotes ?? '', vazio.tiradas.surgeryNotes);
    return { ...draft, notes: notes || undefined, surgeryNotes: surgeryNotes || undefined, tiradas: vazio.tiradas };
  }
  return draft;
}

export interface VoiceQueueItem {
  id: string;
  draft: VoiceDraft;
  /** Atendimento em que a gravação começou (pode ser null fora de um). */
  encounterId: string | null;
  createdAt: number;
  /**
   * Quando foi gravado no prontuário. Existe pra imprimir duas vezes NÃO
   * registrar duas — o médico reimprime por motivos banais (papel enroscou,
   * paciente pediu outra via) e isso não pode virar documento duplicado no
   * histórico.
   */
  registeredAt?: number;
  /**
   * Segunda opinião do Jev (spec 012). `logIds` ligam o card às entradas do
   * diário, pra o que o médico fizer com ele virar o gabarito; `alertas` são
   * os avisos na tela (avisam, não bloqueiam — mesma regra das pendências).
   */
  jev?: { logIds: string[]; alertas: JevAlerta[] };
  /**
   * Spec 016, fase 2: enquanto este pedido foi falado só a sala falou — a
   * lapela do médico ficou calada. Avisa (pode ter sido o paciente), não
   * bloqueia. "Era pedido" tira.
   */
  ditoPelaSala?: boolean;
  /**
   * Falas (transcrição) que puseram conteúdo neste card, na ordem. O botão
   * "Jev decide" (04/11/2026) roda DEPOIS, com o Ao vivo pausado — e precisa
   * da fala de cada dúvida pra decidir.
   */
  falas?: string[];
}

/** Teto das falas guardadas por card (a rota do Jev lê só os últimos 2000). */
const MAX_FALAS_CARD = 2000;

function comFala(item: VoiceQueueItem, fala: string): VoiceQueueItem {
  const falas = [...(item.falas ?? []), fala];
  while (falas.length > 1 && falas.join(' ').length > MAX_FALAS_CARD) falas.shift();
  return { ...item, falas };
}

export interface JevAlerta {
  motivo: 'conversa' | 'tipo';
  /** Trecho de fala que o Jev avaliou. */
  fala: string;
  /** Tipo de documento que o Jev não ouviu (só em `motivo: 'tipo'`). */
  tipo?: JevKind;
}

const queues = new Map<string, VoiceQueueItem[]>();
type Listener = (patientId: string) => void;
const listeners = new Set<Listener>();

function notify(patientId: string) {
  listeners.forEach((fn) => {
    try { fn(patientId); } catch { /* ignore */ }
  });
}

export function subscribeVoiceQueue(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function getVoiceQueue(patientId: string): VoiceQueueItem[] {
  return queues.get(patientId) ?? [];
}

function newId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `v${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
}

export interface AddResult {
  added: number;
  /** Rótulos dos documentos recusados por já existirem iguais na fila. */
  duplicates: string[];
  /** Onde cada documento do ditado foi parar (card novo, pré-ativado ou fundido). */
  placements: Array<{ id: string; draft: VoiceDraft }>;
}

/**
 * Põe os documentos de um ditado na fila do paciente, pulando o que já está lá.
 *
 * A duplicata é por CONTEÚDO (`draftSignature`), não por tipo: ditar a mesma
 * receita duas vezes é engano; ditar duas receitas diferentes no mesmo
 * atendimento é legítimo e passa.
 */
export function addToVoiceQueue(
  patientId: string,
  encounterId: string | null,
  drafts: VoiceDraft[],
  /** Fala que trouxe estes documentos — fica no card pro botão do Jev. */
  fala?: string,
): AddResult {
  let current = getVoiceQueue(patientId);
  const seen = new Set(current.map((i) => draftSignature(i.draft)));
  const duplicates: string[] = [];
  const additions: VoiceQueueItem[] = [];
  const placements: AddResult['placements'] = [];
  let merged = 0;

  // Card pré-ativado ainda vazio do mesmo tipo: o ditado PREENCHE ele (mesmo
  // id, mesma posição na fila) em vez de abrir outro ao lado.
  const preencheVazio = (draft: VoiceDraft): boolean => {
    const vazio = current.find((i) => i.draft.kind === draft.kind && !i.registeredAt && isDraftEmpty(i.draft));
    if (!vazio) return false;
    const cheio = herdaTiradas(vazio.draft, draft);
    current = current.map((i) => (i.id === vazio.id ? { ...i, draft: cheio } : i));
    seen.add(draftSignature(cheio));
    merged += 1;
    placements.push({ id: vazio.id, draft });
    return true;
  };

  for (const draft of drafts) {
    const sig = draftSignature(draft);
    // "Em PDF separado": pedido explícito do médico vence a fusão E a trava
    // de duplicata — ele quer outro papel, mesmo que igual. Um pré-ativado
    // vazio não é "outro papel": é preenchido.
    if (draft.separado) {
      if (preencheVazio(draft)) continue;
      seen.add(sig);
      const id = newId();
      additions.push({ id, draft, encounterId, createdAt: Date.now() });
      placements.push({ id, draft });
      continue;
    }
    if (seen.has(sig)) {
      duplicates.push(draft.label);
      continue;
    }
    if (preencheVazio(draft)) continue;

    // Atestado / laudo: UM card por documento. Mesmo modelo de novo = o mesmo
    // documento (os dias novos trocam os antigos); modelo que resolve o empate
    // de um card aberto ("atestado…" → "…com ciclo") entra NAQUELE card;
    // modelo diferente é outro documento, card novo.
    if (draft.kind === 'atestadoLaudo') {
      const abertos = [...current].reverse().filter((i) => i.draft.kind === 'atestadoLaudo' && !i.registeredAt);
      const id = draft.modelo?.id;
      const mesmo = id ? abertos.find((i) => i.draft.kind === 'atestadoLaudo' && i.draft.modelo?.id === id) : undefined;
      const empate = id && !mesmo
        ? abertos.find((i) => i.draft.kind === 'atestadoLaudo' && !i.draft.modelo && i.draft.opcoes?.some((o) => o.id === id))
        : undefined;
      if (mesmo && mesmo.draft.kind === 'atestadoLaudo') {
        const fundido = mergeTextDocDrafts(mesmo.draft, draft);
        if (!fundido) {
          duplicates.push(draft.label);
          continue;
        }
        current = current.map((i) => (i.id === mesmo.id ? { ...i, draft: fundido } : i));
        seen.add(draftSignature(fundido));
        merged += 1;
        placements.push({ id: mesmo.id, draft });
        continue;
      }
      if (empate) {
        current = current.map((i) => (i.id === empate.id ? { ...i, draft } : i));
        seen.add(sig);
        merged += 1;
        placements.push({ id: empate.id, draft });
        continue;
      }
      seen.add(sig);
      const novoId = newId();
      additions.push({ id: novoId, draft, encounterId, createdAt: Date.now() });
      placements.push({ id: novoId, draft });
      continue;
    }

    // Ditado em sequência funde no card aberto (não registrado) do mesmo
    // tipo, em vez de abrir outro: exames/cirurgia/retorno, medicamentos e
    // refração.
    {
      const alvo = [...current].reverse().find((i) => i.draft.kind === draft.kind && !i.registeredAt);
      if (alvo) {
        const fundido =
          draft.kind === 'solicitacaoExames' && alvo.draft.kind === 'solicitacaoExames'
            ? mergeExamDrafts(alvo.draft, draft)
            : draft.kind === 'receitaMedicamentos' && alvo.draft.kind === 'receitaMedicamentos'
              ? mergeMedDrafts(alvo.draft, draft)
              : draft.kind === 'receitaOculos' && alvo.draft.kind === 'receitaOculos'
                ? mergeLensDrafts(alvo.draft, draft)
                : draft.kind === 'examesAtendimento' && alvo.draft.kind === 'examesAtendimento'
                  ? mergeExamValuesDrafts(alvo.draft, draft)
                  : null;
        if (!fundido) {
          duplicates.push(draft.label);
          continue;
        }
        current = current.map((i) => (i.id === alvo.id ? { ...i, draft: fundido } : i));
        seen.add(draftSignature(fundido));
        merged += 1;
        placements.push({ id: alvo.id, draft });
        continue;
      }
    }

    seen.add(sig);
    const id = newId();
    additions.push({ id, draft, encounterId, createdAt: Date.now() });
    placements.push({ id, draft });
  }

  if (additions.length > 0 || merged > 0) {
    const falou = fala?.trim();
    const tocados = new Set(placements.map((p) => p.id));
    const fila = [...current, ...additions];
    queues.set(
      patientId,
      comIntervaloNaFila(falou ? fila.map((i) => (tocados.has(i.id) ? comFala(i, falou) : i)) : fila),
    );
    notify(patientId);
  }
  return { added: additions.length + merged, duplicates, placements };
}

/**
 * Chega depois do card (a chamada ao Jev é assíncrona). Card que já saiu da
 * fila ou foi registrado nesse meio-tempo: não recebe nada.
 */
export function attachJevResult(patientId: string, id: string, logId: string, alerta: JevAlerta | null): void {
  const current = getVoiceQueue(patientId);
  const alvo = current.find((i) => i.id === id);
  if (!alvo || alvo.registeredAt) return;
  const jev = {
    logIds: [...(alvo.jev?.logIds ?? []), logId],
    alertas: alerta ? [...(alvo.jev?.alertas ?? []), alerta] : alvo.jev?.alertas ?? [],
  };
  queues.set(patientId, current.map((i) => (i.id === id ? { ...i, jev } : i)));
  if (alerta) notify(patientId);
}

/** "Era pedido": o médico descarta o aviso — o Jev errou nesta. */
export function dismissJevAlerts(patientId: string, id: string): void {
  const current = getVoiceQueue(patientId);
  const alvo = current.find((i) => i.id === id);
  if (!alvo?.jev || alvo.jev.alertas.length === 0) return;
  resolveJev(alvo.jev.logIds, 'manteve');
  queues.set(patientId, current.map((i) => (i.id === id ? { ...i, jev: { logIds: alvo.jev!.logIds, alertas: [] } } : i)));
  notify(patientId);
}

/** Spec 016, fase 2: marca os cards que nasceram de fala só da sala. */
export function marcarDitoPelaSala(patientId: string, ids: readonly string[]): void {
  if (ids.length === 0) return;
  const current = getVoiceQueue(patientId);
  if (!current.some((i) => ids.includes(i.id) && !i.registeredAt)) return;
  queues.set(patientId, current.map((i) => (ids.includes(i.id) && !i.registeredAt ? { ...i, ditoPelaSala: true } : i)));
  notify(patientId);
}

/** "Era pedido": o médico tira o aviso de fala da sala. */
export function dispensarDitoPelaSala(patientId: string, id: string): void {
  const current = getVoiceQueue(patientId);
  if (!current.some((i) => i.id === id && i.ditoPelaSala)) return;
  queues.set(patientId, current.map((i) => (i.id === id ? { ...i, ditoPelaSala: false } : i)));
  notify(patientId);
}

/**
 * "Tira a cirurgia": remove só a parte de cirurgia do card de solicitações
 * aberto. Se sobrar exame ou retorno o card fica; senão sai inteiro.
 */
export function stripSurgeriesFromVoiceQueue(patientId: string): string | null {
  const current = getVoiceQueue(patientId);
  const alvo = [...current].reverse().find(
    (i) => i.draft.kind === 'solicitacaoExames' && !i.registeredAt && i.draft.surgeries.length > 0,
  );
  if (!alvo || alvo.draft.kind !== 'solicitacaoExames') return null;
  const d = alvo.draft;
  if (d.exams.length === 0 && !d.retorno) {
    // Sai sem contar como "o médico tirou as Solicitações": ele pediu pra
    // tirar a cirurgia, e o pré-ativado pode voltar na próxima abertura.
    queues.set(patientId, current.filter((i) => i.id !== alvo.id));
    notify(patientId);
    return d.label;
  }
  const card = { exams: d.exams, retorno: d.retorno, surgeries: [] };
  const fundido: ExamDraft = {
    kind: 'solicitacaoExames',
    label: solicitacoesLabel(card),
    ...card,
    ...(d.notes ? { notes: d.notes } : {}),
    gaps: d.gaps.filter((g) => !g.field.startsWith('cirurgias[')),
  };
  queues.set(patientId, current.map((i) => (i.id === alvo.id ? { ...i, draft: fundido } : i)));
  notify(patientId);
  return 'Cirurgia/procedimento';
}

/**
 * Divide um card em dois, no mesmo lugar da fila: o primeiro fica com o id
 * (e o que o Jev disse dele), o segundo entra logo depois com id novo. Usado
 * pra imprimir só uma PARTE do card "Solicitações" ("imprime os exames"): o
 * registro no prontuário é por card, então a parte que não foi pro papel tem
 * que virar outro card, ainda aberto. Devolve o id novo, ou null se o card
 * não existe ou já foi registrado.
 */
export function splitVoiceQueueItem(patientId: string, id: string, first: VoiceDraft, second: VoiceDraft): string | null {
  const current = getVoiceQueue(patientId);
  const idx = current.findIndex((i) => i.id === id);
  if (idx < 0 || current[idx].registeredAt) return null;
  const alvo = current[idx];
  const novoId = newId();
  const { jev: _jev, ...semJev } = alvo;
  queues.set(patientId, [
    ...current.slice(0, idx),
    { ...alvo, draft: first },
    { ...semJev, id: novoId, draft: second },
    ...current.slice(idx + 1),
  ]);
  notify(patientId);
  return novoId;
}

/** Substitui o card (edição manual). O id e o vínculo com o atendimento ficam. */
export function updateVoiceQueueDraft(patientId: string, id: string, draft: VoiceDraft): void {
  const current = getVoiceQueue(patientId);
  if (!current.some((i) => i.id === id)) return;
  queues.set(patientId, comIntervaloNaFila(current.map((i) => (i.id === id ? { ...i, draft } : i))));
  notify(patientId);
}

/** Marca cards como já registrados no prontuário. */
export function markVoiceQueueRegistered(patientId: string, ids: string[]): void {
  const alvo = new Set(ids);
  const current = getVoiceQueue(patientId);
  if (!current.some((i) => alvo.has(i.id) && !i.registeredAt)) return;
  resolveJev(current.filter((i) => alvo.has(i.id) && !i.registeredAt).flatMap((i) => i.jev?.logIds ?? []), 'manteve');
  queues.set(
    patientId,
    current.map((i) => (alvo.has(i.id) && !i.registeredAt ? { ...i, registeredAt: Date.now() } : i)),
  );
  notify(patientId);
}

/**
 * A receita (de óculos ou de medicamentos) foi salva — ou impressa — pelo
 * card da FICHA, onde o ditado a tinha posto: o card aberto do ditado passa
 * a contar como registrado, sem gravar nada — senão o "Prontuário" e o
 * "Registrar no prontuário" gravavam a mesma receita outra vez (09/10/2026).
 */
export function markVoiceReceitaIssued(patientId: string, kind: 'receitaOculos' | 'receitaMedicamentos'): void {
  const ids = getVoiceQueue(patientId)
    .filter((i) => i.draft.kind === kind && !i.registeredAt && !isDraftEmpty(i.draft))
    .map((i) => i.id);
  if (ids.length > 0) markVoiceQueueRegistered(patientId, ids);
}

/**
 * O médico tirou o card (X no card ou "tira a receita de óculos" no ao
 * vivo). Card aberto de um tipo pré-ativável tirado assim NÃO volta sozinho
 * na próxima abertura do ditado deste paciente.
 */
export function removeFromVoiceQueue(patientId: string, id: string): void {
  const current = getVoiceQueue(patientId);
  const alvo = current.find((i) => i.id === id);
  if (!alvo) return;
  if (!alvo.registeredAt) resolveJev(alvo.jev?.logIds ?? [], 'tirou');
  if (!alvo.registeredAt && (VOICE_PRESET_KINDS as readonly string[]).includes(alvo.draft.kind)) {
    const tirados = dismissedPresets.get(patientId) ?? new Set<VoiceDraft['kind']>();
    tirados.add(alvo.draft.kind);
    dismissedPresets.set(patientId, tirados);
  }
  queues.set(patientId, current.filter((i) => i.id !== id));
  notify(patientId);
}

/**
 * Tira da fila os pré-ativados que ficaram vazios — chamado ao registrar no
 * prontuário: vazio não é documento, é só descartado (e pode voltar na
 * próxima abertura, porque não foi o médico que o tirou).
 */
export function discardEmptyVoiceDrafts(patientId: string): void {
  const current = getVoiceQueue(patientId);
  const next = current.filter((i) => i.registeredAt || !isDraftEmpty(i.draft));
  if (next.length !== current.length) {
    queues.set(patientId, next);
    notify(patientId);
  }
}

export function clearVoiceQueue(patientId: string): void {
  dismissedPresets.delete(patientId);
  examesImpressos.delete(patientId);
  if (queues.delete(patientId)) notify(patientId);
}

/** Chamado no logout — nenhuma fila de paciente sobrevive à troca de usuário. */
export function clearAllVoiceQueues(): void {
  const ids = [...queues.keys()];
  queues.clear();
  dismissedPresets.clear();
  examesImpressos.clear();
  ids.forEach(notify);
}

// ─── Folhas de exame já impressas (28/10/2026) ───
//
// Os cards de exame (Exames do atendimento, Biomicroscopia…) imprimem mas não
// são registrados ao imprimir — vão pro prontuário pelo "Prontuário" —, então
// não ganham `registeredAt`. A caneta precisa saber o que já saiu pra não
// repetir a folha a cada aperto: guarda a assinatura do CONTEÚDO
// (`draftSignature`); ditou de novo, é folha nova.

const examesImpressos = new Map<string, Set<string>>();

export function getExamesImpressos(patientId: string): ReadonlySet<string> {
  return examesImpressos.get(patientId) ?? new Set();
}

export function marcarExamesImpressos(patientId: string, assinaturas: readonly string[]): void {
  if (assinaturas.length === 0) return;
  const set = examesImpressos.get(patientId) ?? new Set<string>();
  assinaturas.forEach((a) => set.add(a));
  examesImpressos.set(patientId, set);
}

// ─── Pré-ativação (pedido do Leopoldo, 26/09/2026) ───
//
// "Receita de óculos, de medicamentos, solicitação de exames, solicitação de
// cirurgias e laudos/atestados podem ficar já pré-ativados ao abrir o
// ditado, apenas para serem editados/preenchidos com os dados do áudio."
//
// Ao abrir o ditado, a fila ganha um card VAZIO de cada tipo; o que for
// ditado entra NELE (`addToVoiceQueue` preenche o vazio antes de abrir
// outro). Mapeamento: exames + cirurgias já são UM card, "Solicitações"
// (`solicitacaoExames`). Laudo e atestado ficaram de fora até 08/10/2026,
// quando viraram documento do ditado (`textDocs.ts`): agora o card vazio
// "Atestado / laudo" também abre — e nele dá pra escolher o modelo com um
// clique, sem ditar.

/** Tipos pré-ativados ao abrir o ditado, na ordem em que aparecem. */
export const VOICE_PRESET_KINDS = ['receitaOculos', 'receitaMedicamentos', 'solicitacaoExames', 'atestadoLaudo'] as const;
export type VoicePresetKind = (typeof VOICE_PRESET_KINDS)[number];

/** Rótulo do card enquanto vazio. */
export const VOICE_PRESET_LABEL: Record<VoicePresetKind, string> = {
  receitaOculos: 'Receita de óculos',
  receitaMedicamentos: 'Receita de medicamentos',
  solicitacaoExames: 'Solicitações (exames / cirurgia)',
  atestadoLaudo: TEXT_DOC_EMPTY_LABEL,
};

/** Tipos que o médico tirou da fila, por paciente — a pré-ativação não repõe. */
const dismissedPresets = new Map<string, Set<VoiceDraft['kind']>>();

const campoVazio = () => ({ value: null, heard: '' });
const olhoVazio = (): VoiceEyeRx => ({
  esferica: campoVazio(),
  cilindrica: campoVazio(),
  eixo: campoVazio(),
  adicao: campoVazio(),
  dp: '',
  av: '',
});

/** Card vazio ("Aguardando ditado") de um tipo pré-ativável. */
export function emptyVoiceDraft(kind: VoicePresetKind): VoiceDraft {
  const label = VOICE_PRESET_LABEL[kind];
  switch (kind) {
    case 'receitaOculos':
      return { kind, label, gaps: [], lens: { od: olhoVazio(), oe: olhoVazio(), observacoes: '' } };
    case 'receitaMedicamentos':
      return { kind, label, gaps: [], items: [] };
    case 'solicitacaoExames':
      return { kind, label, gaps: [], exams: [], retorno: null, surgeries: [] };
    case 'atestadoLaudo':
      return emptyTextDocDraft();
  }
}

/**
 * Desligar sem deploy (console do navegador, depois recarregar):
 * `localStorage.setItem('dratento.voice.preset', '0')`.
 */
const LS_PRESET = 'dratento.voice.preset';
export function isVoicePresetEnabled(): boolean {
  try {
    return localStorage.getItem(LS_PRESET) !== '0';
  } catch {
    return true;
  }
}

/**
 * Põe os cards vazios na fila do paciente. Não duplica: tipo que já tem card
 * aberto (não registrado) — ditado antes, vazio de uma abertura anterior —
 * fica como está; tipo que o médico tirou nesta fila também.
 * Devolve quantos entraram.
 */
export function preactivateVoiceQueue(
  patientId: string,
  encounterId: string | null,
  kinds: readonly VoicePresetKind[] = VOICE_PRESET_KINDS,
): number {
  const current = getVoiceQueue(patientId);
  const tirados = dismissedPresets.get(patientId);
  const additions: VoiceQueueItem[] = kinds
    .filter((k) => !tirados?.has(k) && !current.some((i) => i.draft.kind === k && !i.registeredAt))
    .map((k) => ({ id: newId(), draft: emptyVoiceDraft(k), encounterId, createdAt: Date.now() }));
  if (additions.length === 0) return 0;
  queues.set(patientId, [...current, ...additions]);
  notify(patientId);
  return additions.length;
}

// Emulador (só `--mode emulator` — o build de produção não leva): põe cards
// na fila sem ditar, pra conferir a tela do card no navegador (Playwright /
// Claude). Ex.: `__dratentoVoz.addToVoiceQueue(patientId, null, [draft])`.
if (import.meta.env.MODE === 'emulator' && typeof window !== 'undefined') {
  (window as unknown as { __dratentoVoz?: unknown }).__dratentoVoz = { addToVoiceQueue, getVoiceQueue };
}
